import { useItemSet } from '@exam-management/common/item-set-provider';
import { removeHtmlTag } from '@exam-management/common/utils';
import { FormLeavingListener } from '@exam-management/components';
import { parseContentForApi, useParseContentForItemSet } from '@exam-management/rich-text-editor';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import type { PropsWithChildren } from '@shared/utils';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

// TODO: refactor this to reuse at item-set-creation later
const ITEM_SET_NAME_MAX_LENGTH = 256;

const ItemSetQuestionDetailSchema = z.object({
  details: z.object({
    name: z
      .string()
      .trim()
      .min(1, { message: 'กรุณากรอกชื่อ item set' })
      .max(ITEM_SET_NAME_MAX_LENGTH, {
        message: `ชื่อ item set ต้องมีความยาวไม่เกิน ${ITEM_SET_NAME_MAX_LENGTH} ตัวอักษร`,
      }),
    paragraph: z.string().refine(
      (value) => {
        const details = removeHtmlTag(value, {
          customTag: 'p',
        }).trim();
        return details.length >= 1;
      },
      {
        message: 'กรุณากรอกบทความ',
      },
    ),
  }),
});

export type ItemSetQuestionDetailFormInput = z.infer<typeof ItemSetQuestionDetailSchema>;

export default function ItemSetQuestionEditForm(props: Readonly<PropsWithChildren>) {
  const { children } = props;

  const { itemSet, save } = useItemSet();
  const { showSnackbar } = useSnackbar();

  const formContext = useForm<ItemSetQuestionDetailFormInput>({
    resolver: zodResolver(ItemSetQuestionDetailSchema),
  });

  const { parseContentForItemSet } = useParseContentForItemSet({ questionGroupId: itemSet.id });

  const handleResetForm = useCallback(async () => {
    formContext.reset({
      details: {
        name: itemSet.name,
        paragraph: await parseContentForItemSet(itemSet.metadata?.paragraph ?? ''),
      },
    });
  }, [formContext, itemSet.name, itemSet.metadata?.paragraph, parseContentForItemSet]);

  useEffect(() => {
    handleResetForm();
  }, [handleResetForm]);

  const handleEditItemSetQuestionFormSubmit = async (data: ItemSetQuestionDetailFormInput) => {
    await save(
      {
        name: data.details.name,
        paragraph: parseContentForApi(data.details.paragraph),
      },
      {
        onCompleted: () => {
          showSnackbar({
            message: 'Item Set has been saved',
            variant: 'success',
          });
        },
        onError: () => {
          showSnackbar({
            message: 'Save failed, please try again',
            variant: 'error',
          });
        },
      },
    );

    formContext.reset({
      details: {
        name: data.details.name,
        paragraph: data.details.paragraph,
      },
    });
  };

  useEffect(() => {
    if (formContext.formState.isSubmitSuccessful) {
      formContext.reset(formContext.getValues());
    }
  }, [formContext.formState.isSubmitSuccessful]);

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(handleEditItemSetQuestionFormSubmit)}>
        {children}
      </Form>
      <FormLeavingListener onSubmit={handleEditItemSetQuestionFormSubmit} />
    </FormProvider>
  );
}
